// CookiePolicy.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './LegalPages.css';
import logo from '../images/logo.png';

function CookiePolicy() {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user'));
  const user_id = user?.user_id;

  const cookieTypes = [
    {
      category: "Essential Cookies",
      purpose: "Required for basic site functionality",
      examples: ["Session management", "Authentication", "Security"],
      duration: "Session / Persistent",
      required: true
    },
    {
      category: "Functional Cookies",
      purpose: "Remember your preferences and settings",
      examples: ["Language preference", "Theme selection", "Login information"],
      duration: "Up to 1 year",
      required: false
    },
    {
      category: "Analytics Cookies",
      purpose: "Help us understand how visitors interact with our site",
      examples: ["Page views", "Time spent", "Feature usage"],
      duration: "Up to 2 years",
      required: false
    },
    {
      category: "Marketing Cookies",
      purpose: "Used for advertising and promotional purposes",
      examples: ["Ad personalization", "Campaign tracking"],
      duration: "Up to 90 days",
      required: false
    }
  ];

  return (
    <div className="legal-page light-theme">
      {/* Navigation */}
      <nav className="legal-navbar">
        <div className="legal-nav-container">
          <button
            className="legal-brand"
            onClick={() => navigate(user_id ? '/home' : '/')}
          >
            <img src={logo} alt='Marshall AI Logo' className='legal-logo' />
            <span>Marshall AI</span>
          </button>
          <button
            className="legal-back-btn"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="legal-container">
        <div className="legal-content">
          <h1 className="legal-title">Cookie Policy</h1>
          <p className="legal-last-updated">Last Updated: April 12, 2026</p>

          <section className="legal-section">
            <h2>1. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are placed on your device when you visit a website. 
              They are widely used to make websites work more efficiently and provide information 
              to the site owners.
            </p>
            <p>
              Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your 
              device after you close your browser, while session cookies are deleted once you close 
              your browser.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. How We Use Cookies</h2>
            <p>We use cookies for the following purposes:</p>
            <ul>
              <li>To authenticate users and maintain sessions</li>
              <li>To remember your preferences (theme, language)</li>
              <li>To analyze site traffic and usage patterns</li>
              <li>To improve our services and user experience</li>
              <li>To provide security features</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Types of Cookies We Use</h2>
            
            <div className="cookie-table-container">
              <table className="cookie-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Purpose</th>
                    <th>Examples</th>
                    <th>Duration</th>
                    <th>Required</th>
                  </tr>
                </thead>
                <tbody>
                  {cookieTypes.map((cookie, index) => (
                    <tr key={index}>
                      <td><strong>{cookie.category}</strong></td>
                      <td>{cookie.purpose}</td>
                      <td>{cookie.examples.join(", ")}</td>
                      <td>{cookie.duration}</td>
                      <td>
                        {cookie.required ? 
                          <span className="badge-required">Required</span> : 
                          <span className="badge-optional">Optional</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="legal-section">
            <h2>4. Third-Party Cookies</h2>
            <p>
              We may also use third-party cookies from trusted partners for analytics and 
              authentication purposes:
            </p>
            <ul>
              <li><strong>Google Analytics:</strong> Helps us understand user behavior and improve our service</li>
              <li><strong>Google OAuth:</strong> Used for secure authentication via Google accounts</li>
            </ul>
            <p>
              These third parties may collect information about your online activities over time 
              and across different websites.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Your Cookie Choices</h2>
            <p>You have several options to control or limit how cookies are used:</p>
            
            <h3>5.1 Browser Settings</h3>
            <p>
              Most browsers allow you to refuse or accept cookies. You can typically:
            </p>
            <ul>
              <li>Delete existing cookies</li>
              <li>Block all cookies</li>
              <li>Allow only certain types of cookies</li>
              <li>Set preferences for specific websites</li>
            </ul>
            <p>
              Note that blocking essential cookies may prevent you from using certain features 
              of our Service.
            </p>

            <h3>5.2 Cookie Consent</h3>
            <p>
              When you first visit our site, you will see a cookie consent banner allowing you to 
              accept or customize your cookie preferences.
            </p>

            <h3>5.3 Do Not Track</h3>
            <p>
              Some browsers have a "Do Not Track" feature. We currently do not respond to 
              Do Not Track signals.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Cookie Duration</h2>
            <ul>
              <li><strong>Session Cookies:</strong> Expire when you close your browser</li>
              <li><strong>Persistent Cookies:</strong> Remain for a set period (1 day to 2 years)</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Managing Cookies on Different Browsers</h2>
            <p>Here's how to manage cookies on popular browsers:</p>
            <ul>
              <li><strong>Google Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
              <li><strong>Mozilla Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
              <li><strong>Microsoft Edge:</strong> Settings → Privacy, search, and services → Cookies</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Changes to This Cookie Policy</h2>
            <p>
              We may update this Cookie Policy from time to time. Changes will be posted on this page 
              with an updated "Last Updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. More Information</h2>
            <p>
              For more information about how we handle your data, please refer to our 
              <a href="/privacy" className="legal-link"> Privacy Policy</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Contact Us</h2>
            <p>If you have questions about our use of cookies, contact us at:</p>
            <ul>
              <li><strong>Email:</strong>c84289798@gmail.com</li>
              <li><strong>Subject Line:</strong> Cookie Policy Inquiry</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;